import { logger } from '@adornis/base/logging.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import { type LogistikComponentShippingDetails } from '@chancenlogistik/client/components/logistik-component-shipping-details';
import { LogistikRoutes } from '@chancenlogistik/client/routes';
import '@chancenwerk/components/chancen-component-card-title.js';
import {
  LogistikIndividualOrderItem,
  LogistikOrder,
  LogistikOrderShippingDetails,
} from '@chancenwerk/db/LogistikOrder.js';
import type { ChromiumButton } from '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-textarea.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @customElement logistik-page-individual-order
 */
@customElement('logistik-page-individual-order')
export class LogistikPageIndividualOrder extends ChemistryLitElement {
  private newOrder = new LogistikOrder({
    shippingDetails: new LogistikOrderShippingDetails({}),
    items: [new LogistikIndividualOrderItem({})],
  });
  @state() private readonly _form = new FormController(this, this.newOrder, {
    success: async order => {
      try {
        await ChromiumDialog.waitFor(order.create());
        void ChromiumDialog.alert(
          'Individuelle Bestellung erfolgreich aufgegeben. Sollte ein Freigabe-Prozess notwendig sein erhalten Sie eine E-Mail sobald die Bestellung freigegeben oder abgelehnt wurde.',
        );
        this.newOrder = new LogistikOrder({
          shippingDetails: new LogistikOrderShippingDetails({}),
          items: [new LogistikIndividualOrderItem({})],
        });
        this._form.document = this.newOrder;
        this._shippingRef.value?.clearSelection();
        this.requestUpdate();
      } catch (e) {
        logger.error(e, 'There was an error completing individual order');
        ChromiumDialog.alert('Es gab ein Problem beim Aufgeben der Bestellung: ' + (e as Error).message);
      }
    },
    error: err => console.warn('error: ', err),
  });
  @state() private readonly _shippingRef = createRef<LogistikComponentShippingDetails>();

  private _item(i: number) {
    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title>
        Produkt #${i + 1}
        <cr-button
          slot="right"
          mode="icon"
          tone="error"
          icon="delete"
          ?disabled=${this.newOrder.items.length === 1}
          @click=${() => {
            this.newOrder.items.splice(i, 1);
            this.requestUpdate();
          }}
        ></cr-button>
      </chancen-component-card-title>
      <cr-input placeholder="Produktbezeichnung" ${this._form.field('items', i, 'name')}></cr-input>
      <cr-input placeholder="Produktart" ${this._form.field('items', i, 'type')}></cr-input>
      <cr-flex horizontal space="md">
        <cr-input
          flex
          placeholder="Preis pro Stück"
          type="number"
          postfix="€"
          ${this._form.field('items', i, 'price')}
        ></cr-input>
        <cr-input flex placeholder="Anzahl" type="number" ${this._form.field('items', i, 'quantity')}></cr-input>
      </cr-flex>
      <cr-input placeholder="Bestell-Link" ${this._form.field('items', i, 'link')}></cr-input>
      <chancen-component-card-title> Weitere Angaben </chancen-component-card-title>
      <cr-flex space="md">
        <cr-input placeholder="Verwendungszweck" ${this._form.field('items', i, 'purpose')}></cr-input>
        <cr-textarea
          ${this._form.field('items', i, 'explanation')}
          placeholder="Begründung (max. 1000 Zeichen)"
        ></cr-textarea>
        <cr-checkbox
          ${this._form.field('items', i, 'acceptAlternative')}
          label="Ich bin damit einverstanden, ein gleichwertiges Produkt einer anderen Produktgattung geliefert zu bekommen. (Farbe, Maße und Artikelzustand können hierbei abweichen)"
        ></cr-checkbox>
      </cr-flex>
    </cr-card>`;
  }

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle="Individuelle Bestellung"
          backRoute=${LogistikRoutes.landing}
        ></logistik-component-base-header>
        <cr-flex space="lg">
          <cr-text>
            Hier können Sie eine individuelle Bestellung, die nicht explizit in den anderen Kategorien aufgeführt ist,
            tätigen. Individuelle Bestellungen durchlaufen einen Freigabeprozess.
          </cr-text>
          <cr-flex space="md">
            ${repeat(
              this.newOrder.items,
              (item, i) => i,
              (item, i) => this._item(i),
            )}
            <cr-flex
              ${css({
                alignItems: 'end',
              })}
            >
              <cr-button
                icon="add"
                ${css({ maxWidth: '250px' })}
                @click=${() => {
                  this.newOrder.items.push(new LogistikIndividualOrderItem({}));
                  this.requestUpdate();
                }}
                >Weiteres Produkt</cr-button
              >
            </cr-flex>
            <cr-card padding="md" space="md">
              <chancen-component-card-title> Versand Informationen </chancen-component-card-title>
              <logistik-component-shipping-details
                ${this._form.field('shippingDetails')}
                ${ref(this._shippingRef)}
              ></logistik-component-shipping-details>
            </cr-card>
          </cr-flex>
          <cr-flex horizontal end space="md">
            <cr-button
              icon="send"
              @click=${async (e: MouseEvent) => {
                const currentTarget = e.currentTarget as ChromiumButton;
                try {
                  await this._shippingRef.value?._form.validate({ mouseEvent: e });
                  await this._form.validate({ mouseEvent: e });
                } catch {
                  /* error will be handled in corresponding formcontroller methods, but we need this to stop execution after one fails */
                  currentTarget.loading = false;
                }
              }}
            >
              Bestellung aufgeben
            </cr-button>
          </cr-flex>
        </cr-flex>
      </logistik-component-base-page-layout>
    `;
  }
}
